/**
 * Formats a phone number string for display.
 * Handles US phone numbers (10 digits) and international phone numbers.
 * Supports extensions (ext, x, extension).
 *
 * @param phone - Raw phone number string (may include country code, spaces, dashes, extensions, etc.)
 * @returns Formatted phone number or the original string if it doesn't match expected formats
 *
 * Examples:
 * - "1234567890" -> "(123) 456-7890"
 * - "+11234567890" -> "(123) 456-7890"
 * - "+441234567890" -> "+44 1234 567 890"
 * - "+331234567890" -> "+33 1234 567 890"
 * - "1234567890 ext 123" -> "(123) 456-7890 ext 123"
 */
export function formatPhoneNumber(phone: string | null): string {
  if (!phone) return "";

  const extensionMatch = phone.match(/(.+?)\s*(?:ext|x|extension)[\s.:]*(\d+)/i);
  let basePhone = phone;
  let extension = "";

  if (extensionMatch) {
    basePhone = extensionMatch[1].trim();
    extension = ` ext ${extensionMatch[2]}`;
  }

  const cleaned = basePhone.replace(/[^\d+]/g, "");

  if (cleaned.length < 7) return phone;

  const usPhoneMatch = cleaned.match(/^\+?1?(\d{3})(\d{3})(\d{4})$/);
  if (usPhoneMatch) {
    const [, areaCode, exchange, number] = usPhoneMatch;
    return `(${areaCode}) ${exchange}-${number}${extension}`;
  }

  if (cleaned.startsWith("+") && cleaned.length > 10) {
    let countryCode = "";
    let rest = "";

    if (cleaned.length === 12) {
      countryCode = cleaned.slice(1, 2);
      rest = cleaned.slice(2);
    } else if (cleaned.length >= 12 && cleaned.length <= 13) {
      countryCode = cleaned.slice(1, 3);
      rest = cleaned.slice(3);
    } else {
      countryCode = cleaned.slice(1, 4);
      rest = cleaned.slice(4);
    }

    const groups = [];
    let remaining = rest;

    if (remaining.length >= 4) {
      groups.push(remaining.slice(0, 4));
      remaining = remaining.slice(4);
    } else if (remaining.length >= 3) {
      groups.push(remaining.slice(0, 3));
      remaining = remaining.slice(3);
    }

    while (remaining.length > 0) {
      const groupSize = remaining.length >= 3 ? 3 : remaining.length;
      groups.push(remaining.slice(0, groupSize));
      remaining = remaining.slice(groupSize);
    }

    return `+${countryCode} ${groups.join(" ")}${extension}`;
  }

  return phone;
}

export const normalizePhoneNumber = (phone: string): string => {
  const digits = phone.replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
};

/**
 * For Algolia/search: if the query is phone-shaped (digits and phone punctuation only),
 * return national digits so it matches indexed phone fields. Otherwise return the query unchanged.
 */
export const normalizePhoneSearchQuery = (query: string): string => {
  const trimmed = query.trim();
  if (!trimmed) return trimmed;
  if (!/^[\d\s().+-]+$/.test(trimmed)) return trimmed;

  const normalized = normalizePhoneNumber(trimmed);
  return normalized || trimmed;
};
